import { BigNumber, ContractTransaction, ethers, Signer } from 'ethers';
import { action, makeAutoObservable, runInAction } from 'mobx';
import { App } from './app.store';
import { LoadableStore } from './loadable.store';
import { AuctionStore } from './auction.store';
import * as Firebase from '../services/firebase.service';
import { EthGasSpeed, EthGasStation, EthGasWait, GasAndTime } from '../model/eth-gasstation';
import { Ethereumish, NFTYAuctionMini, AuctionArtifactMini } from 'nfty-common/lib/contracts/'

declare global {
    interface Window {
        ethereum: Ethereumish;
        web3: ethers.providers.ExternalProvider;
    }
}

export class WalletError extends Error { }

export class WalletStore implements LoadableStore {
    appStore: App;
    auctionStore: AuctionStore;
    provider?: ethers.providers.JsonRpcProvider;
    defaultSigner?: Signer;
    contract?: NFTYAuctionMini;
    walletAddress?: string;
    walletFunds?: BigNumber;

    hasValidProvider = false;
    isConnectedToChain = false;

    constructor(appStore: App) {
        this.appStore = appStore;
        this.auctionStore = appStore.auctionStore;
        appStore.registerStore('wallet', this);

        makeAutoObservable(this, {
            appStore: false,
            auctionStore: false,
            submitBid: false,
            connectWithWallet: action
        });
    }

    async load(): Promise<boolean> {
        const { network } = this.appStore.config;

        const provider = (network)
            ? new ethers.providers.JsonRpcProvider(network)
            : (window.ethereum) ? new ethers.providers.Web3Provider(window.ethereum) : null;

        if (provider) {
            runInAction(() => {
                this.hasValidProvider = true;
                this.provider = provider;
            });

            provider.on('connect', this.connectWithWallet);
            provider.on('accountsChanged', () => {
                this.provider = new ethers.providers.Web3Provider(window.ethereum);
                this.connectWithWallet();
            });
            provider.on('chainChanged', window.location.reload);
        } else {
            console.log('Unable to connect to ethereum network! No provider found');
        }

        return true;
    }

    async userConnect() {
        try {
            if (this.hasValidProvider) {
                await window.ethereum.sendAsync({ method: 'eth_requestAccounts' }, (a) => {
                    runInAction(() => {
                        this.isConnectedToChain = true;
                        this.connectWithWallet();
                    });
                });
            }
        } catch (error) {
            this.isConnectedToChain = false;
        }
    }

    /**
     * For whatever reason, this api responds with price gWei * 10, which means
     * before getting a wei estimate, we need to divide by 10, then convert to
     * wei:
     *  1 gWei = 1000000000 wei
     *  y wei = x / 10 * 1000000000
     * @returns gas price in gWei
     */
    async recommendGas(speed: EthGasSpeed = 'average', wait: EthGasWait = 'avgWait'): Promise<GasAndTime> {
        const response = await fetch('https://ethgasstation.info/json/ethgasAPI.json', {
            mode: 'cors'
        });

        const data = await response.json() as EthGasStation;
        const number = ethers.BigNumber.from(data[speed]);
        return {
            gasPrice: number.div(10).mul(1000000000),
            waitTime: data[wait]
        };
    }

    async submitBid(bid: string, gas: GasAndTime): Promise<ContractTransaction | undefined> {
        if (this.contract && this.defaultSigner && this.provider) {
            const balance = await this.defaultSigner.getBalance();
            const value = ethers.utils.parseEther(bid);

            if (value.gte(balance)) {
                throw new WalletError('Insufficient funds');
            }

            try {
                await this.provider.getNetwork();

                const tx = await this.contract.bid({ value, gasPrice: gas.gasPrice });
                await tx.wait();

                await Firebase.onSubmitBid();
                await this.refreshWalletInfo(this.defaultSigner);

                return tx;
            } catch (error) {
                if (error.data?.message) {
                    const revertMessage = error.data.message.split('revert').pop();
                    throw new WalletError(revertMessage);
                }
                throw new WalletError('Transaction failed');
            }
        } else {
            throw new WalletError('You must allow NFTY to communicate with your wallet before you can bid');
        }
    }

    async connectWithWallet() {
        if (this.provider) {
            const { auctionAddress } = this.appStore.config;

            this.defaultSigner = this.provider.getSigner();
            this.contract = new ethers.Contract(auctionAddress, AuctionArtifactMini.abi, this.defaultSigner) as unknown as NFTYAuctionMini;

            await this.refreshWalletInfo(this.defaultSigner);
        }
    }

    async refreshWalletInfo(signer?: Signer) {
        const s = signer || this.defaultSigner;
        if (s) {
            runInAction(async () => {
                try {
                    this.walletAddress = await s.getAddress();
                    this.walletFunds = await s.getBalance();
                } catch (error) {
                    console.log(error);
                }
            });
        }
    }

}